/* Hero video — picks ONE source per breakpoint and never fetches the other.
   ---------------------------------------------------------------------------
   Why this needs JS at all:

   A <video src> begins fetching as soon as the element is parsed. CSS cannot
   stop it — display:none, a media query, a zero-size box, none of it prevents
   the request. And `media` on a <source> inside <video> is not dependable:
   the resource-selection algorithm evaluates it once, support has been patchy,
   and it is never re-evaluated. So a mobile visitor would still pull the
   desktop file.

   The element therefore ships with NO src at all — just data-mobile-src and
   data-desktop-src — and exactly one is assigned here, after matchMedia has
   decided which. One request, always the right one.

   LCP: the poster image stays eager with fetchpriority=high, so it is still
   requested first and still wins the paint. The video starts buffering at
   DOMContentLoaded with preload=auto — at 1-2MB it does not meaningfully
   crowd the poster, and waiting for window load meant waiting on every other
   asset on the page, which is what made the hero sit still before it moved.

   With no image set at all the section carries data-no-poster; nothing
   changes for it, it simply has no poster to protect.

   With JS off, or under prefers-reduced-motion, no video loads at all and the
   still stands — which is a correct hero, not a broken one.

   The breakpoint IS re-evaluated when the viewport crosses 990px, and the
   section is re-activated on shopify:section:load. Both matter more than the
   saved request: without them the theme editor shows the desktop clip while
   previewing mobile, and shows nothing at all after a setting change — which
   reads as "the two pickers hold the same value" when they do not.

   Crossing the breakpoint costs one extra download. That is the correct
   trade: it only happens on a real orientation change or an editor toggle,
   and showing the wrong crop is worse than fetching once more.
*/

(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  function activate(video) {
    if (window.matchMedia(REDUCED).matches) {
      video.setAttribute('data-hero-video-ready', 'reduced');
      return; // poster stands in
    }

    var wide = window.matchMedia(DESKTOP).matches;
    var want = wide ? 'desktop' : 'mobile';
    // already showing the right one for this breakpoint
    if (video.getAttribute('data-hero-video-ready') === want) return;

    var src = video.getAttribute(wide ? 'data-desktop-src' : 'data-mobile-src');
    // fall back to whichever one the merchant did set
    if (!src) src = video.getAttribute(wide ? 'data-mobile-src' : 'data-desktop-src');
    if (!src || video.src === src) return;

    video.setAttribute('data-hero-video-ready', want);
    video.src = src;

    var played = video.play();
    if (played && played.catch) played.catch(function () { /* autoplay refused; poster stands */ });
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll('video[data-hero-video]');
    for (var i = 0; i < videos.length; i++) activate(videos[i]);
  }

  /* Start at DOMContentLoaded, not window load.

     Hero videos on this store are kept to 1–2MB, so they are not a meaningful
     competitor for bandwidth against the poster — and waiting for `load` means
     waiting on every image, script and third-party tag on the page, which is
     what actually made the hero read as static before it moved.

     Measured on throttled 4G before shipping this; see the commit for the
     numbers. If hero videos ever grow past a few MB this trade should be
     re-checked, because at that size the competition stops being negligible.

     The window-load pass is kept only as a safety net for a video that arrives
     late (an app injecting markup, say). activate() is idempotent per
     breakpoint, so it costs nothing when everything already started. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); }, { once: true });
  } else {
    run();
  }

  if (document.readyState !== 'complete') {
    window.addEventListener('load', function () { run(); }, { once: true });
  }

  // Crossing the breakpoint swaps the source. addEventListener on the query
  // rather than a resize handler, so it fires once per crossing instead of
  // on every pixel of a drag.
  var mq = window.matchMedia(DESKTOP);
  if (mq.addEventListener) mq.addEventListener('change', function () { run(); });
  else if (mq.addListener) mq.addListener(function () { run(); });

  // Theme editor: a setting change re-renders the section, giving a brand new
  // <video> with no src. Without this it never loads and the merchant sees an
  // empty hero after every edit.
  document.addEventListener('shopify:section:load', function (e) { run(e.target); });
})();
